import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Modal } from 'antd';
import useWorkspaces from '../hooks/useWorkspaces';
import { createWorkspace } from '../services/WorkspacesService';
import '../components/sandbox/sandbox.css';
import './SandboxHome.css';
import './Workspaces.css';

const ROLE_TEXT = { owner: 'Owner', editor: 'Editor', viewer: 'Viewer' };
const plural = (count, one, many = `${one}s`) => `${count} ${count === 1 ? one : many}`;

/**
 * Workspaces (vfs-service docs/workspaces.md): groups of projects with their own members and brain. The list of the
 * person's workspaces (name, their role, how many projects and members) and "New workspace"; each opens its page
 * (pages/Workspace.jsx). Where the gateway has no workspaces, the page says so.
 */
export default function Workspaces() {
  const navigate = useNavigate();
  const { workspaces, available, loading, error, reload } = useWorkspaces();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [createError, setCreateError] = useState(null);

  const create = async() => {
    setSaving(true);
    setCreateError(null);
    try {
      const workspace = await createWorkspace({ name: name.trim(), description: description.trim() });
      setCreating(false);
      setName('');
      setDescription('');
      await reload();
      navigate(`/workspaces/${encodeURIComponent(workspace.id)}`);
    } catch (failed) {
      setCreateError(failed.message || 'The workspace could not be created.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sbx-home">
      <div className="sbx-home-inner workspaces">
        <div className="workspaces-head">
          <div className="sbx-home-title">
            <h1>Workspaces</h1>
            <p>A workspace groups projects. Everyone in it reaches all its projects, and it has its own brain, shared by them.</p>
          </div>
          {available && <Button onClick={() => setCreating(true)} type="primary">New workspace</Button>}
        </div>

        {loading && <p className="workspaces-note">Loading your workspaces…</p>}
        {!loading && error && <p className="sbx-action-error" role="alert">{error.message}</p>}
        {!loading && !error && !available && <p className="workspaces-note">Workspaces are not available here.</p>}
        {!loading && available && workspaces.length === 0 && (
          <div className="sbx-card workspaces-empty">
            <strong>No workspaces yet</strong>
            <span>Create one, then move your projects into it and add the people you work with.</span>
          </div>
        )}
        {!loading && workspaces.length > 0 && (
          <ul aria-label="Your workspaces" className="workspaces-list">
            {workspaces.map((workspace) => (
              <li key={workspace.id}>
                <button className="sbx-card workspaces-item" onClick={() => navigate(`/workspaces/${encodeURIComponent(workspace.id)}`)} type="button">
                  <span className="workspaces-item-name">
                    <strong>{workspace.name}</strong>
                    <span className="sbx-pill">{ROLE_TEXT[workspace.role] || workspace.role}</span>
                  </span>
                  {workspace.description && <span className="workspaces-item-description">{workspace.description}</span>}
                  <span className="workspaces-item-meta">
                    {plural(workspace.projectCount || 0, 'project')} · {plural((workspace.memberCount || 0) + 1, 'person', 'people')}
                    {workspace.role !== 'owner' && workspace.owner ? ` · owned by ${workspace.owner.name || workspace.owner.email}` : ''}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Modal
        destroyOnHidden
        footer={[
          <Button key="cancel" onClick={() => setCreating(false)}>Cancel</Button>,
          <Button disabled={!name.trim()} key="create" loading={saving} onClick={create} type="primary">Create</Button>,
        ]}
        onCancel={() => setCreating(false)}
        open={creating}
        title="New workspace"
      >
        <label className="workspaces-field">
          <span>Name</span>
          <Input autoFocus maxLength={80} onChange={(event) => setName(event.target.value)} onPressEnter={() => name.trim() && create()} placeholder="e.g. Payments team" value={name} />
        </label>
        <label className="workspaces-field">
          <span>Description (optional)</span>
          <Input.TextArea maxLength={500} onChange={(event) => setDescription(event.target.value)} rows={2} value={description} />
        </label>
        {createError && <p className="sbx-action-error" role="alert">{createError}</p>}
      </Modal>
    </div>
  );
}
